import { Fragment, useId, useMemo } from 'react';
import type { PropsWithChildren } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { AnimatePresence } from '@tamagui/animate-presence';
import { isWeb } from '@tamagui/core';
import {
  Dialog as TamaguiDialog,
  DialogContent as TamaguiDialogContent,
  DialogOverlay as TamaguiDialogOverlay,
  useDialogContext,
} from '@tamagui/dialog';
import type {
  DialogContentProps as TamaguiDialogContentProps,
  DialogOverlayProps as TamaguiDialogOverlayProps,
  DialogProps as TamaguiDialogProps,
} from '@tamagui/dialog';
import { useWindowDimensions } from 'react-native';

import type { AnimationName } from '../../animationTypes';
import { tamaguiElement } from '../../helpers/tamaguiElement';
import { getOpacityTokenValue, getPercentageTokenValue } from '../../helpers/tokens';
import { Box } from '../Box';
import type { BoxProps } from '../Box';
import { CloseButton } from '../Button';
import { BackgroundBlur } from './BackgroundBlur';
import { BricksDialogContext } from './context';

const { dialog: dialogTokens } = tokens;
const BricksDialogContent = tamaguiElement<TamaguiDialogContentProps>(TamaguiDialogContent);
const BricksDialogOverlay = tamaguiElement<TamaguiDialogOverlayProps>(TamaguiDialogOverlay);

export type DialogProps = Pick<TamaguiDialogProps, 'onOpenChange'> &
  Pick<BoxProps, 'analyticsName' | 'trackImpression'> & {
    /**
     * Provide custom animation to dialog. Defaults to "accelerateDecelerateFastest"
     */
    animation?: AnimationName;
    /**
     * State representing whether the dialog is open or not (required)
     */
    isOpen: boolean;
    /**
     * Callback to change the isOpen state (required)
     */
    setIsOpen: (isOpen: boolean) => void;
    /**
     * Whether the dialog can be dismissed by clicking the close button or the
     * overlay or dragging the handle on touch devices. Defaults to true. If
     * false, the close button and the handle will be hidden.
     */
    dismissable?: boolean;
    /**
     * Whether the overlay should be dark or light. Defaults to "dark".
     */
    overlayColor?: 'dark' | 'light' | 'transparent';
    /**
     * Whether to show the close button (X) in the top right corner. Defaults to
     * true. When false, the close button is hidden but the dialog can still be
     * dismissed by clicking the overlay (if dismissable is true).
     */
    showCloseButton?: boolean;
  };

/**
 * Dialog is used to display attention-grabbing content on top of the rest of
 * the page. The component is responsive: on small screens, it displays as a
 * sheet that can be dragged up and down, while on larger screens, it displays
 * as a modal that can be dismissed by clicking the overlay or the close button.
 * If the component should not be dismissable, you can pass
 * `dismissable={false}`.
 *
 * Dialog is a controlled component, so it's required that you keep track of the
 * state of the component externally and pass both the `isOpen` and `setIsOpen`
 * props. (Otherwise, the dialog will never open or close.)
 *
 * `Dialog.Content` is a required child of Dialog. It renders the content you
 * want to display in a scrollable container. You must pass `title` and
 * `description` props to `Dialog.Content` for a11y. If you need to visually
 * hide either the title or description, you can pass `hideTitle` or
 * `hideDescription`, respectively, and pass custom content as a child of
 * `Dialog.Content`. If, on the other hand, you want to display the title or
 * description but customize how it gets rendered, you can pass `titleProps` or
 * `descriptionProps`, respectively. Note that both title and description have
 * default `$largerThanSM` props, so if you want to customize how they render on
 * larger screens, you'll need to pass `$largerThanSM` as part of `titleProps`
 * or `descriptionProps`.
 *
 * Dialog also takes an optional child for displaying actions: `Dialog.Actions`.
 * The action component should go after `Dialog.Content` so it can be displayed
 * outside of the scrollable container.
 *
 * The complete anatomy of Dialog looks something like this (depending on which
 * actions you need, if any):
 *
 * ```tsx
 * const MyComponent = () => {
 *   const [isOpen, setIsOpen] = useState(false);
 *   return (
 *     <Dialog isOpen={isOpen} setIsOpen={setIsOpen}>
 *       <Dialog.Content title="Dialog Title" description="Dialog Description" />
 *       <Dialog.Actions
 *         checkboxProps={{ label: 'Checkbox Label', value: true, analyticsName: '' }}
 *         buttonOneProps={{ variant: 'secondary', analyticsName: '', label: 'Save', onPress: () => {} }}
 *         buttonTwoProps={{ variant: 'primary', analyticsName: '', label: 'Cancel', onPress: () => {} }}
 *       />
 *     </Dialog>
 *   );
 * };
 * ```
 *
 * If you pass a non-empty `analyticsName` and `trackImpression={true}` to the
 * Dialog component, the component will track view events ("boxViewEvent").
 * Analytics props can also be passed to the buttons inside the action
 * components via the respective button props.
 */
export const Dialog = ({
  isOpen,
  animation = 'accelerateDecelerateFastest',
  setIsOpen,
  overlayColor = 'dark',
  analyticsName,
  trackImpression,
  dismissable = true,
  showCloseButton = true,
  children,
}: PropsWithChildren<DialogProps>) => {
  const reactId = useId();
  const dialogScope = useMemo(() => `bricks-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`, [reactId]);
  const dialogContext = useMemo(() => ({ scope: dialogScope }), [dialogScope]);

  const overlayProps =
    overlayColor === 'dark'
      ? { backgroundColor: dialogTokens.color.scrim }
      : overlayColor === 'transparent'
        ? { backgroundColor: undefined }
        : {
            backgroundColor: dialogTokens.color.scrimInverse,
            children: (
              <BackgroundBlur
                blurRadius={dialogTokens.backgroundBlur.default}
                style={{ position: 'absolute', top: 0, bottom: 0, right: 0, left: 0 }}
              />
            ),
          };

  const maxHeightTokenValue = getPercentageTokenValue(dialogTokens.percentage.maxHeight);
  const minHeightTokenValue = getPercentageTokenValue(dialogTokens.percentage.minHeightWidth);

  const dimensions = useWindowDimensions();

  const maxSheetHeight = useMemo((): BoxProps['maxHeight'] => {
    const percent = parseInt(maxHeightTokenValue, 10);
    if (isWeb) {
      return `${percent}dvh`;
    }
    return 0.01 * percent * dimensions.height;
  }, [dimensions.height, maxHeightTokenValue]);

  const minSheetHeight = useMemo((): BoxProps['minHeight'] => {
    const percent = parseInt(minHeightTokenValue, 10);
    if (isWeb) {
      return `${percent}dvh`;
    }
    return 0.01 * percent * dimensions.height;
  }, [dimensions.height, minHeightTokenValue]);

  return (
    <BricksDialogContext.Provider value={dialogContext}>
      <TamaguiDialog
        scope={dialogScope}
        modal
        open={isOpen}
        // This seems to be how clicking the overlay closes the dialog. If
        // dismissable is false, then clicking the overlay won't close the dialog.
        onOpenChange={dismissable ? setIsOpen : undefined}
      >
        <AnimatePresence>
          {isOpen && (
            <Fragment key="dialog">
              <DialogPortal scope={dialogScope}>
                <BricksDialogOverlay
                  scope={dialogScope}
                  key="overlay"
                  animation={animation}
                  animateOnly={['opacity']}
                  enterStyle={{ opacity: 0 }}
                  exitStyle={{ opacity: 0 }}
                  {...overlayProps}
                />
                <BricksDialogContent
                  scope={dialogScope}
                  key="content"
                  animation={animation}
                  animateOnly={['opacity', 'transform']}
                  enterStyle={{ opacity: 0, y: 20 }}
                  exitStyle={{ opacity: 0, y: 20 }}
                  overflow="hidden"
                  padding={0}
                  borderWidth={0}
                  backgroundColor={dialogTokens.color.background.default}
                  borderRadius={dialogTokens.cornerRadius.desktop.default}
                  minWidth={getPercentageTokenValue(dialogTokens.percentage.minHeightWidth)}
                  minHeight={minHeightTokenValue}
                  maxHeight={maxHeightTokenValue}
                  $largerThanSM={{
                    maxWidth: getPercentageTokenValue(dialogTokens.percentage.maxWidth),
                  }}
                >
                  <Box
                    analyticsName={analyticsName}
                    trackImpression={trackImpression}
                    flexGrow={1}
                    flexShrink={1}
                    position="relative"
                  >
                    {dismissable && showCloseButton && (
                      <TamaguiDialog.Close scope={dialogScope} asChild>
                        <CloseButton
                          size="large"
                          analyticsName={`${analyticsName}-close`}
                          position="absolute"
                          zIndex={1}
                          top={0}
                          right={0}
                          margin={dialogTokens.padding.actions.closeIconButton.desktop}
                        />
                      </TamaguiDialog.Close>
                    )}

                    {children}
                  </Box>
                </BricksDialogContent>
              </DialogPortal>

              <TamaguiDialog.Adapt when="smallerThanMD">
                <TamaguiDialog.Sheet
                  modal
                  dismissOnSnapToBottom={dismissable}
                  moveOnKeyboardChange
                  dismissOnOverlayPress={dismissable}
                  disableDrag={!dismissable}
                  animation={animation}
                  snapPointsMode="fit"
                >
                  <TamaguiDialog.Sheet.Overlay
                    key="overlay"
                    animation={animation}
                    animateOnly={['opacity']}
                    enterStyle={{ opacity: 0 }}
                    exitStyle={{ opacity: 0 }}
                    {...overlayProps}
                  />
                  {dismissable && (
                    <TamaguiDialog.Sheet.Handle
                      borderRadius={dialogTokens.cornerRadius.mobile.handle}
                      height={dialogTokens.mobile.handle.height}
                      width={dialogTokens.mobile.handle.width}
                      alignSelf="center"
                      marginLeft={0}
                      marginRight={0}
                      marginBottom={dialogTokens.margin.mobile.handle.bottom}
                      backgroundColor={
                        overlayColor === 'dark'
                          ? dialogTokens.color.background.mobile.handle
                          : dialogTokens.color.background.mobile.handleInverse
                      }
                      opacity={getOpacityTokenValue(dialogTokens.opacity.mobileHandle)}
                    />
                  )}
                  <TamaguiDialog.Sheet.Frame
                    backgroundColor={dialogTokens.color.background.default}
                    borderTopLeftRadius={dialogTokens.cornerRadius.mobile.default}
                    borderTopRightRadius={dialogTokens.cornerRadius.mobile.default}
                    maxHeight={maxSheetHeight}
                    minHeight={minSheetHeight}
                  >
                    <TamaguiDialog.Adapt.Contents />
                  </TamaguiDialog.Sheet.Frame>
                </TamaguiDialog.Sheet>
              </TamaguiDialog.Adapt>
            </Fragment>
          )}
        </AnimatePresence>
      </TamaguiDialog>
    </BricksDialogContext.Provider>
  );
};

const DialogPortal = ({ scope, children }: PropsWithChildren<{ scope: string }>) => {
  const { descriptionId, titleId } = useDialogContext(scope);

  return (
    <TamaguiDialog.Portal scope={scope} aria-describedby={descriptionId} aria-labelledby={titleId}>
      {children}
    </TamaguiDialog.Portal>
  );
};
